import { zodResolver } from '@hookform/resolvers/zod'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { useBreakPoint } from '@repo/toolkit/use-break-point'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Button from '@repo/ui/button'
import { EmailIcon } from '@repo/ui/icons'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useLocation } from 'react-router-dom'
import { z, ZodType } from 'zod'

import { ssoLogin } from '../../../api/auth/sso-login'
import { Paths } from '../../../lib/constants/path'
import useTranslate from '../../../lib/hooks/use-translate'
import { SsoLoginInput } from '../../../lib/types/models/auth/login'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { FormControl, FormField, FormItem, TranslatedForm } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import { pathnameToUserType } from './utils'

const formSchema = (language: AvailableLanguages): ZodType<SsoLoginInput> =>
  z.object({
    email: createStringSchema('email', { isEmail: true, language })
  })

export const SsoLoginForm = () => {
  const device = useBreakPoint()
  const { pathname } = useLocation()
  const isMobile = device === 'mobile'
  const { navigate } = useNavigator()
  const { t, currentLanguage } = useTranslate()

  const { mutate, isPending } = useMutation({
    mutationFn: ssoLogin,
    onError: showAxiosError,
    onSuccess: () => {
      navigate(`${Paths.AUTH_CHECK_EMAIL_PAGE}?user=${pathnameToUserType[pathname]}`)
    }
  })

  const form = useForm<SsoLoginInput>({
    resolver: zodResolver(formSchema(currentLanguage)),
    defaultValues: {
      email: ''
    },
    mode: FORM_MODE
  })

  function onSubmit(values: SsoLoginInput) {
    mutate(values)
  }

  return (
    <TranslatedForm<SsoLoginInput> form={form}>
      <form onSubmit={form.handleSubmit(onSubmit)} autoComplete={'off'}>
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <ControlledInput
                  {...field}
                  wrapperClassName="bg-white"
                  type="email"
                  placeholder="E-mail"
                  label={!isMobile ? 'E-mail' : undefined}
                  icon={EmailIcon}
                  autoComplete={'new-email'}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <Button
          loading={isPending}
          type="submit"
          label={t('staff.personal.space.connect')}
          className={'w-full mt-6 mobile:bg-primary-dark-hover mobile:h-11'}
        />
      </form>
    </TranslatedForm>
  )
}
